import { AppPackage } from 'src/app/core/models/app.model';
import { Package } from 'src/app/core/models/package.model';
import { PackageType } from 'src/app/core/models/package-type.model';

// The standalone product (360 Depict today) is a package type whose apps are all no-device.
// Mirrors isStandaloneAppSet in the backend, and reads the group rather than a hardcoded type
// id, so a new standalone package created from the UI needs no code change here.
//
// Both reassignment modals apply the same rule, so it lives here instead of in each of them:
// the general one (reassign-modal) and the one that hands a package down from a base entity
// (no-device-reassign-modal).

export const NO_DEVICE_GROUP = 'no-device';

const PACKAGE_STATUS_INACTIVE = 5;
const PACKAGE_CATEGORY_DEMO = 2;

export function isStandalonePackageType(
  packageTypes: PackageType[] | null | undefined,
  packageTypeId?: number | null
): boolean {
  const type = (packageTypes ?? []).find((pt: PackageType) => pt.id === Number(packageTypeId));
  const appPackages = type?.appPackages ?? [];
  return appPackages.length > 0
    && appPackages.every((ap: AppPackage) => ap.app?.group === NO_DEVICE_GROUP);
}

// Inactive and past its renewal window: it no longer occupies the slot, so the sale is not
// rejected — but the shop did have one, which is what the same-group warning exists to flag.
export function isFullyDead(_package: Package): boolean {
  return _package?.packageStatusId === PACKAGE_STATUS_INACTIVE
    && (!_package.renewalPersDate || new Date(_package.renewalPersDate) <= new Date());
}

// Does the destination already hold a standalone package that is dead? That is what stops a
// distributor handing over another one — which would grant the shop a fresh trial period —
// while still letting an admin through after confirming it.
//
// Demos are left out on purpose: they are trials by definition, and the general reassign modal
// already drops them from the list it checks. Only an operative counts.
export function hasDeadStandalonePackage(
  packageTypes: PackageType[] | null | undefined,
  packages: Package[] | null | undefined,
  excludePackageId?: number | null
): boolean {
  return (packages ?? []).some((pack: Package) =>
    pack.id !== excludePackageId
    && (pack.packageCategoryId ?? pack.category?.id) !== PACKAGE_CATEGORY_DEMO
    && isStandalonePackageType(packageTypes, pack.packageTypeId)
    && isFullyDead(pack));
}
